import { scoreColor } from '../../shared/designTokens.js';
import { roundedOverallScore } from '../../shared/scoreDisplay.js';

const R = 50;
const CIRC = 2 * Math.PI * R; // ≈ 314.16
const TRACK_LENGTH = CIRC * 0.75; // 235.6 — 3/4 of the ring

/**
 * Circular score ring. Geometry matches docs/reference/CritWise_Dashboard.html:
 *   - viewBox 120x120, r=50, strokeWidth 10
 *   - track dash "235.6 78.5" rotated 135deg (starts lower-left)
 *   - fill dash proportional to score / 100 × 235.6
 *   - colored by scoreColor() so the ring agrees with the PDF
 */
export function ScoreRing({ score, color: colorOverride, size = 120, ariaLabel }) {
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const fillLength = (clamped / 100) * TRACK_LENGTH;
  // PDF-POLISH-1 D14: caller may pass a verdict-driven color so the ring
  // tracks the engine verdict instead of the score band. Falls back to the
  // score-band default for surfaces that don't have a verdict in hand.
  const color = colorOverride || scoreColor(clamped);
  // FIX-B Part 3: the ONE display rounding (shared with the recent-reports
  // list and the PDF cover) — every surface shows the identical integer.
  const rounded = roundedOverallScore(clamped);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      role="img"
      aria-label={ariaLabel || `Overall schedule health score: ${rounded} of 100`}
    >
      <circle
        cx="60"
        cy="60"
        r={R}
        fill="none"
        stroke="#DEE2E6"
        strokeWidth="10"
        strokeDasharray={`${TRACK_LENGTH.toFixed(1)} ${CIRC - TRACK_LENGTH}`}
        strokeLinecap="round"
        transform="rotate(135 60 60)"
      />
      <circle
        cx="60"
        cy="60"
        r={R}
        fill="none"
        stroke={color}
        strokeWidth="10"
        strokeDasharray={`${fillLength.toFixed(1)} ${CIRC}`}
        strokeLinecap="round"
        transform="rotate(135 60 60)"
        style={{ transition: 'stroke-dasharray 0.8s ease' }}
      />
      <text
        x="60"
        y="58"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fontSize: '38px',
          fontWeight: 800,
          fill: color,
          fontFamily: 'var(--font-body)',
        }}
      >
        {rounded}
      </text>
      <text
        x="60"
        y="78"
        textAnchor="middle"
        style={{
          fontSize: '12px',
          fill: '#ADB5BD',
          fontFamily: 'var(--font-body)',
        }}
      >
        /100
      </text>
    </svg>
  );
}
